import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { emailAutomationService, EmailAutomationData } from "@/services/emailAutomation.service";
import { getKlaviyoOauthUrl, getKlaviyoStatus, type KlaviyoStatus } from "@/services/klaviyo.service";
import { getOmnisendOauthUrl, getOmnisendStatus, type OmnisendStatus } from "@/services/omnisend.service";
import { toast } from "react-hot-toast";
import SkeletonCard from "@/components/SkeletonCard";
import { AlertCircle } from "lucide-react";
import { usePermissions } from "@/contexts/PermissionsContext";
import { PlanFeature } from "@/types/permissions";
import { useNavigate } from "react-router-dom";
import { useBrand } from "@/contexts/BrandContext/hooks";

interface EmailAutomationFormProps {
  onEditEmail: (emailType: EmailType) => void;
}

export type EmailType =
  | "welcome"
  | "newSale"
  | "payout"
  | "approval"
  | "invite";

export interface EmailTemplateData {
  subject: string;
  body: string;
}

// Email automation data interface is now imported from service

const defaultData: Partial<EmailAutomationData> = {
  newAffiliateSignup: false, // Non-welcome automation - default to false
  newAffiliateSale: false, // Non-welcome automation - default to false
  welcomeNewAffiliate: false, // Welcome automation - default to false
  newSale: false, // Non-welcome automation - default to false
  payoutSent: false, // Non-welcome automation - default to false
  createAffiliateAccountInvite: false, // Non-welcome automation - default to false
  affiliateApproval: false, // Non-welcome automation - default to false
  omnisendApiKey: "",
  klaviyoApiKey: "",
};

export function EmailAutomationForm({
  onEditEmail,
}: EmailAutomationFormProps) {
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const { brandDetails } = useBrand();
  const [formData, setFormData] = useState<Partial<EmailAutomationData>>(defaultData);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [savingField, setSavingField] = useState<string | null>(null);
  const [omnisendStatus, setOmnisendStatus] = useState<OmnisendStatus | null>(null);
  const [connectingOmnisend, setConnectingOmnisend] = useState(false);
  const [klaviyoStatus, setKlaviyoStatus] = useState<KlaviyoStatus | null>(null);
  const [connectingKlaviyo, setConnectingKlaviyo] = useState(false);

  // Check if email automation is enabled for this brand
  const isEmailAutomationEnabled = brandDetails?.isEmailAutomationEnabled ?? true;

  // Check if user has non-welcome email automation permission
  // Use permission from backend response or from permissions context
  const hasNonWelcomePermission = formData.hasNonWelcomeAutomationPermission ?? 
    (permissions && permissions.features.includes(PlanFeature.NON_WELCOME_EMAIL_AUTOMATION));

  // Non-welcome email automation fields
  const nonWelcomeFields: (keyof EmailAutomationData)[] = [
    'newSale',
    'payoutSent',
    'affiliateApproval',
    'newAffiliateSignup',
    'newAffiliateSale',
    'createAffiliateAccountInvite',
  ];

  // Load email automation data on component mount
  useEffect(() => {
    const loadEmailAutomation = async () => {
      try {
        setIsLoadingData(true);
        const data = await emailAutomationService.getEmailAutomation();
        setFormData(data);
      } catch (error) {
        console.error("Error loading email automation:", error);
        toast.error("Failed to load email automation settings");
      } finally {
        setIsLoadingData(false);
      }
    };

    loadEmailAutomation();
    getKlaviyoStatus()
      .then(setKlaviyoStatus)
      .catch(() => setKlaviyoStatus(null));
    getOmnisendStatus()
      .then(setOmnisendStatus)
      .catch(() => setOmnisendStatus(null));
  }, []);

  const handleCheckboxChange = async (field: keyof EmailAutomationData, value: boolean) => {
    // Check if email automation is disabled for this brand
    if (!isEmailAutomationEnabled) {
      toast.error("Email automation is currently disabled for your brand. Please contact support to enable this feature.");
      return;
    }

    // Check if this is a non-welcome automation field and user doesn't have permission
    if (nonWelcomeFields.includes(field as keyof EmailAutomationData) && !hasNonWelcomePermission && value === true) {
      toast.error("This feature requires an upgrade. Please upgrade your plan to access non-welcome email automations.");
      navigate("/brand/pricing?upgradeRequired=true&feature=NON_WELCOME_EMAIL_AUTOMATION");
      return;
    }

    // Update local state immediately for UI responsiveness
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Set loading state for this specific field
    setSavingField(field);

    try {
      // Auto-save the change
      await emailAutomationService.updateEmailAutomation({
        [field]: value,
      });
    } catch (error: any) {
      console.error(`Error saving ${field}:`, error);
      // Check if it's a 403 error (permission denied)
      if (error?.response?.status === 403) {
        toast.error("This feature is not available on your plan. Please upgrade to access this feature.");
        navigate("/brand/pricing?upgradeRequired=true&feature=NON_WELCOME_EMAIL_AUTOMATION");
      } else {
        toast.error(`Failed to save ${field} setting`);
      }
      
      // Revert the change on error
      setFormData((prev) => ({
        ...prev,
        [field]: !value,
      }));
    } finally {
      setSavingField(null);
    }
  };

  const handleOmnisendApiKeyRemove = async () => {
    setSavingField("omnisendApiKey");
    try {
      await emailAutomationService.updateEmailAutomation({ omnisendApiKey: "" });
      setFormData((prev) => ({ ...prev, omnisendApiKey: "" }));
      toast.success("Omnisend API key removed");
    } catch (error) {
      console.error("Error removing Omnisend API key:", error);
      toast.error("Failed to remove the Omnisend API key");
    } finally {
      setSavingField(null);
    }
  };

  const handleOmnisendConnect = async () => {
    setConnectingOmnisend(true);
    try {
      const { url } = await getOmnisendOauthUrl();
      window.location.assign(url);
    } catch {
      toast.error("Could not start the Omnisend connection");
      setConnectingOmnisend(false);
    }
  };

  const handleKlaviyoApiKeyRemove = async () => {
    setSavingField("klaviyoApiKey");
    try {
      await emailAutomationService.updateEmailAutomation({ klaviyoApiKey: "" });
      setFormData((prev) => ({ ...prev, klaviyoApiKey: "" }));
      toast.success("Klaviyo API key removed");
    } catch (error) {
      console.error("Error removing Klaviyo API key:", error);
      toast.error("Failed to remove the Klaviyo API key");
    } finally {
      setSavingField(null);
    }
  };

  const handleKlaviyoConnect = async () => {
    setConnectingKlaviyo(true);
    try {
      const { url } = await getKlaviyoOauthUrl();
      window.location.assign(url);
    } catch {
      toast.error("Could not start the Klaviyo connection");
      setConnectingKlaviyo(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isEmailAutomationEnabled) {
      toast.error("Email automation is currently disabled for your brand. Please contact support to enable this feature.");
      return;
    }
    
    try {
      await emailAutomationService.updateEmailAutomation(formData);
      toast.success("Email automation settings saved successfully");
    } catch (error) {
      console.error("Error saving email automation:", error);
      toast.error("Failed to save email automation settings");
    } finally {
    }
  };

  if (isLoadingData) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-foreground">Email Automation</h1>
          <p className="text-muted-foreground mt-2">
            Configure email notifications and templates for your affiliate
            program.
          </p>
        </div>
        <div className="space-y-6">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-foreground">Email Automation</h1>
        <p className="text-muted-foreground mt-2">
          Configure email notifications and templates for your affiliate
          program.
        </p>
      </div>

      {/* Restriction Banner */}
      {!isEmailAutomationEnabled && (
        <div className="mb-6 rounded-lg bg-amber-50 border border-amber-200 p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-amber-900 mb-1">
                Email Automation Disabled
              </h3>
              <p className="text-sm text-amber-800">
                Email automation is currently disabled for your brand. All features on this page are disabled. Please contact support to enable this feature.
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">

        {/* Ambassador Emails */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Affiliate Emails</CardTitle>
            <CardDescription>
              Sent to your affiliates.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="welcomeNewAffiliate"
                  checked={formData.welcomeNewAffiliate || false}
                  onCheckedChange={(checked) =>
                    handleCheckboxChange("welcomeNewAffiliate", checked as boolean)
                  }
                  disabled={savingField === "welcomeNewAffiliate" || !isEmailAutomationEnabled}
                  className="data-[state=checked]:bg-brand-600 data-[state=checked]:border-brand-600 border-border focus:ring-brand-500 disabled:opacity-50"
                />
                <label
                  htmlFor="welcomeNewAffiliate"
                  className="text-sm font-medium"
                >
                  Welcome new affiliate
                </label>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onEditEmail("welcome")}
                disabled={!isEmailAutomationEnabled}
                className="border-brand-600 text-brand-600 hover:bg-brand-50 hover:border-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Edit
              </Button>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 flex-1">
                <div
                  title={!hasNonWelcomePermission ? "Upgrade to access" : undefined}
                  className={!hasNonWelcomePermission ? "cursor-not-allowed" : ""}
                >
                  <Checkbox
                    id="newSale"
                    checked={formData.newSale || false}
                    onCheckedChange={(checked) =>
                      handleCheckboxChange("newSale", checked as boolean)
                    }
                    disabled={savingField === "newSale" || !hasNonWelcomePermission || !isEmailAutomationEnabled}
                    className="data-[state=checked]:bg-brand-600 data-[state=checked]:border-brand-600 border-border focus:ring-brand-500 disabled:opacity-50"
                  />
                </div>
                <div className="flex-1">
                  <label htmlFor="newSale" className={`text-sm font-medium ${!hasNonWelcomePermission || !isEmailAutomationEnabled ? 'text-muted-foreground' : ''}`}>
                    New sale
                  </label>
                  {!isEmailAutomationEnabled && (
                    <p className="text-xs text-muted-foreground mt-0.5">Feature disabled</p>
                  )}
                  {isEmailAutomationEnabled && !hasNonWelcomePermission && (
                    <p className="text-xs text-muted-foreground mt-0.5">Upgrade to enable</p>
                  )}
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!isEmailAutomationEnabled) {
                    toast.error("Email automation is currently disabled for your brand.");
                    return;
                  }
                  if (!hasNonWelcomePermission) {
                    navigate("/brand/pricing?upgradeRequired=true&feature=NON_WELCOME_EMAIL_AUTOMATION");
                    return;
                  }
                  onEditEmail("newSale");
                }}
                disabled={!hasNonWelcomePermission || !isEmailAutomationEnabled}
                className="border-brand-600 text-brand-600 hover:bg-brand-50 hover:border-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Edit
              </Button>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 flex-1">
                <div
                  title={!hasNonWelcomePermission ? "Upgrade to access" : undefined}
                  className={!hasNonWelcomePermission ? "cursor-not-allowed" : ""}
                >
                  <Checkbox
                    id="payoutSent"
                    checked={formData.payoutSent || false}
                    onCheckedChange={(checked) =>
                      handleCheckboxChange("payoutSent", checked as boolean)
                    }
                    disabled={savingField === "payoutSent" || !hasNonWelcomePermission || !isEmailAutomationEnabled}
                    className="data-[state=checked]:bg-brand-600 data-[state=checked]:border-brand-600 border-border focus:ring-brand-500 disabled:opacity-50"
                  />
                </div>
                <div className="flex-1">
                  <label htmlFor="payoutSent" className={`text-sm font-medium ${!hasNonWelcomePermission || !isEmailAutomationEnabled ? 'text-muted-foreground' : ''}`}>
                    Payout sent
                  </label>
                  {!isEmailAutomationEnabled && (
                    <p className="text-xs text-muted-foreground mt-0.5">Feature disabled</p>
                  )}
                  {isEmailAutomationEnabled && !hasNonWelcomePermission && (
                    <p className="text-xs text-muted-foreground mt-0.5">Upgrade to enable</p>
                  )}
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!isEmailAutomationEnabled) {
                    toast.error("Email automation is currently disabled for your brand.");
                    return;
                  }
                  if (!hasNonWelcomePermission) {
                    navigate("/brand/pricing?upgradeRequired=true&feature=NON_WELCOME_EMAIL_AUTOMATION");
                    return;
                  }
                  onEditEmail("payout");
                }}
                disabled={!hasNonWelcomePermission || !isEmailAutomationEnabled}
                className="border-brand-600 text-brand-600 hover:bg-brand-50 hover:border-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Edit
              </Button>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 flex-1">
                <div
                  title={!hasNonWelcomePermission ? "Upgrade to access" : undefined}
                  className={!hasNonWelcomePermission ? "cursor-not-allowed" : ""}
                >
                  <Checkbox
                    id="affiliateApproval"
                    checked={formData.affiliateApproval || false}
                    onCheckedChange={(checked) =>
                      handleCheckboxChange("affiliateApproval", checked as boolean)
                    }
                    disabled={savingField === "affiliateApproval" || !hasNonWelcomePermission || !isEmailAutomationEnabled}
                    className="data-[state=checked]:bg-brand-600 data-[state=checked]:border-brand-600 border-border focus:ring-brand-500 disabled:opacity-50"
                  />
                </div>
                <div className="flex-1">
                  <label
                    htmlFor="affiliateApproval"
                    className={`text-sm font-medium ${!hasNonWelcomePermission || !isEmailAutomationEnabled ? 'text-muted-foreground' : ''}`}
                  >
                    Affiliate approval
                  </label>
                  {!isEmailAutomationEnabled && (
                    <p className="text-xs text-muted-foreground mt-0.5">Feature disabled</p>
                  )}
                  {isEmailAutomationEnabled && !hasNonWelcomePermission && (
                    <p className="text-xs text-muted-foreground mt-0.5">Upgrade to enable</p>
                  )}
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  if (!isEmailAutomationEnabled) {
                    toast.error("Email automation is currently disabled for your brand.");
                    return;
                  }
                  if (!hasNonWelcomePermission) {
                    navigate("/brand/pricing?upgradeRequired=true&feature=NON_WELCOME_EMAIL_AUTOMATION");
                    return;
                  }
                  onEditEmail("approval");
                }}
                disabled={!hasNonWelcomePermission || !isEmailAutomationEnabled}
                className="border-brand-600 text-brand-600 hover:bg-brand-50 hover:border-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Edit
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Omnisend Integration */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Omnisend Integration</CardTitle>
            <CardDescription>
              Connect your Omnisend account
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              {omnisendStatus?.connected ? (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm">
                    Connected with your Omnisend login
                    {omnisendStatus.accountName ? ` (${omnisendStatus.accountName})` : ""}.
                  </p>
                  <Button type="button" variant="outline" size="sm" onClick={() => navigate("/brand/integrations")}>
                    Manage
                  </Button>
                </div>
              ) : (
                <>
                  {formData.omnisendApiKey ? (
                    <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                      <AlertCircle size={16} className="mt-0.5 shrink-0" />
                      <p>
                        You're syncing with a pasted API key ({formData.omnisendApiKey}). Omnisend now connects through
                        a secure login instead. Connect below and the pasted key is removed automatically.
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {omnisendStatus?.status === "NEEDS_RECONNECT"
                        ? "Omnisend stopped accepting the connection. Reconnect to keep syncing affiliates."
                        : "Connect with your Omnisend login to sync new affiliates and send their activity to Omnisend."}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      onClick={handleOmnisendConnect}
                      disabled={connectingOmnisend || !isEmailAutomationEnabled}
                    >
                      {omnisendStatus?.status === "NEEDS_RECONNECT" ? "Reconnect Omnisend" : "Connect Omnisend"}
                    </Button>
                    {formData.omnisendApiKey && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleOmnisendApiKeyRemove}
                        disabled={savingField === "omnisendApiKey" || !isEmailAutomationEnabled}
                      >
                        Remove key
                      </Button>
                    )}
                  </div>
                </>
              )}
              <p className="text-xs text-muted-foreground">
                Not currently on Omnisend? <a href="https://your.omnisend.com/JKGW4Q" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:text-brand-700 underline">Start your free trial today</a>.
              </p>
            </div>

            <div className="space-y-2 pt-2 border-t">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="omnisendSendWelcomeEmail"
                  checked={formData.omnisendSendWelcomeEmail ?? false}
                  onCheckedChange={(checked) =>
                    handleCheckboxChange("omnisendSendWelcomeEmail", checked as boolean)
                  }
                  disabled={savingField === "omnisendSendWelcomeEmail" || !isEmailAutomationEnabled}
                  className="data-[state=checked]:bg-brand-600 data-[state=checked]:border-brand-600 border-border focus:ring-brand-500 disabled:opacity-50"
                />
                <label
                  htmlFor="omnisendSendWelcomeEmail"
                  className="text-sm font-medium cursor-pointer"
                >
                  Send Welcome Email
                </label>
              </div>
              <p className="text-xs text-muted-foreground">
                When enabled, Omnisend will automatically send a welcome email to new affiliates when they are added to your list.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Klaviyo Integration */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Klaviyo Integration</CardTitle>
            <CardDescription>
              Connect your Klaviyo account
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              {klaviyoStatus?.connected ? (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm">
                    Connected with your Klaviyo login
                    {klaviyoStatus.accountName ? ` (${klaviyoStatus.accountName})` : ""}.
                  </p>
                  <Button type="button" variant="outline" size="sm" onClick={() => navigate("/brand/integrations")}>
                    Manage
                  </Button>
                </div>
              ) : (
                <>
                  {formData.klaviyoApiKey ? (
                    <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                      <AlertCircle size={16} className="mt-0.5 shrink-0" />
                      <p>
                        You're syncing with a pasted API key ({formData.klaviyoApiKey}). Klaviyo now connects through a
                        secure login instead. Connect below and the pasted key is removed automatically.
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {klaviyoStatus?.status === "NEEDS_RECONNECT"
                        ? "Klaviyo stopped accepting the connection. Reconnect to keep syncing affiliates."
                        : "Connect with your Klaviyo login to sync new affiliates and send their activity to Klaviyo."}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      onClick={handleKlaviyoConnect}
                      disabled={connectingKlaviyo || !isEmailAutomationEnabled}
                    >
                      {klaviyoStatus?.status === "NEEDS_RECONNECT" ? "Reconnect Klaviyo" : "Connect Klaviyo"}
                    </Button>
                    {formData.klaviyoApiKey && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleKlaviyoApiKeyRemove}
                        disabled={savingField === "klaviyoApiKey" || !isEmailAutomationEnabled}
                      >
                        Remove key
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="space-y-2 pt-2 border-t">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="klaviyoSendWelcomeEmail"
                  checked={formData.klaviyoSendWelcomeEmail ?? false}
                  onCheckedChange={(checked) =>
                    handleCheckboxChange("klaviyoSendWelcomeEmail", checked as boolean)
                  }
                  disabled={savingField === "klaviyoSendWelcomeEmail" || !isEmailAutomationEnabled}
                  className="data-[state=checked]:bg-brand-600 data-[state=checked]:border-brand-600 border-border focus:ring-brand-500 disabled:opacity-50"
                />
                <label
                  htmlFor="klaviyoSendWelcomeEmail"
                  className="text-sm font-medium cursor-pointer"
                >
                  Send Welcome Email
                </label>
              </div>
              <p className="text-xs text-muted-foreground">
                When enabled, Klaviyo will automatically send a welcome email to new affiliates when they are added to your list.
              </p>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
